"use client";

import { apiPlugin, RichTextSchema, storyblokInit } from "@storyblok/react/rsc";
import { cloneDeep } from "lodash";
import { ReactElement } from "react";

import Backlink from "./Backlink";
import Button from "./Button";
import Carousel from "./Carousel";
import CatalogTeaser from "./CatalogTeaser";
import Container from "./Container";
import CustomFallbackComponent from "./CustomFallbackComponent";
import Email from "./emails/Email";
import ExperienceSection from "./ExperienceSection";
import FeaturedAccommodations from "./FeaturedAccommodations";
import FeaturedCampsites from "./FeaturedCampsites";
import FeaturedCategories from "./FeaturedCategories";
import FeaturedItems from "./FeaturedItems";
import FeaturedPitches from "./FeaturedPitches";
import Grid from "./Grid";
import Banner from "./header/Banner";
import BannerItem from "./header/BannerItem";
import Config from "./header/Config";
import MenuButton from "./header/MenuButton";
import MenuLink from "./header/MenuLink";
import Accordion from "./landingpage/Accordion";
import AccordionItem from "./landingpage/AccordionItem";
import AwardItem from "./landingpage/AwardItem";
import Awards from "./landingpage/Awards";
import Box from "./landingpage/Box";
import BoxGrid from "./landingpage/BoxGrid";
import Contact from "./landingpage/Contact";
import ContactTeaser from "./landingpage/ContactTeaser";
import IiQCheck from "./landingpage/IiqCheck";
import ImageGallery from "./landingpage/ImageGallery";
import List from "./landingpage/List";
import Map from "./landingpage/Map";
import OpeningHours from "./landingpage/OpeningHours";
import PriceList from "./landingpage/PriceList";
import PriceListBoxes from "./landingpage/PriceListBoxes";
import PriceListItem from "./landingpage/PriceListItem";
import Reviews from "./landingpage/Reviews";
import SpecialOpeningHours from "./landingpage/SpecialOpeningHours";
import TabItem from "./landingpage/TabItem";
import Table from "./landingpage/Table";
import TableGrid from "./landingpage/TableGrid";
import Tabs from "./landingpage/Tabs";
import TextImage from "./landingpage/TextImage";
import TextList from "./landingpage/TextList";
import TextListBoxes from "./landingpage/TextListBoxes";
import Travel from "./landingpage/Travel";
import Page from "./Page";
import Accommodation from "./pages/Accommodation";
import AccommodationOverview from "./pages/AccommodationOverview";
import Booking from "./pages/Booking";
import Campsite from "./pages/Campsite";
import CampsiteOverview from "./pages/CampsiteOverview";
import Careers from "./pages/Carreers";
import Catalog from "./pages/Catalog";
import Category from "./pages/Category";
import CategoryOverview from "./pages/CategoryOverview";
import CycleRoute from "./pages/CycleRoute";
import CycleRouteOverview from "./pages/CycleRouteOverview";
import Event from "./pages/Event";
import EventOverview from "./pages/EventOverview";
import Experience from "./pages/Experience";
import Experiences from "./pages/Experiences";
import Faq from "./pages/Faq";
import Job from "./pages/Job";
import JobOverview from "./pages/JobOverview";
import News from "./pages/News";
import Newsletter from "./pages/Newsletter";
import NewsOverview from "./pages/NewsOverview";
import Offer from "./pages/Offer";
import OfferOverview from "./pages/OfferOverview";
import Office from "./pages/Office";
import Pitch from "./pages/Pitch";
import Prices from "./pages/Prices";
import Restaurants from "./pages/Restaurants";
import SpecialOffer from "./pages/SpecialOffer";
import Stage from "./Stage";
import ImageTeaser from "./teasers/ImageTeaser";
import OfferTeaser from "./teasers/OfferTeaser";
import RestaurantTeaser from "./teasers/RestaurantTeaser";
import TextTeaser from "./teasers/TextTeaser";
import BodyText from "./typography/BodyText";
import Headline from "./typography/Headline";
import RichText from "./typography/RichText";

const mySchema = cloneDeep(RichTextSchema);

storyblokInit({
  accessToken: process.env.storyblokApiToken,
  use: [apiPlugin],
  apiOptions: {
    region: "eu",
  },
  richText: {
    schema: mySchema,
    resolver: (component, blok) => {
      switch (component) {
        case "table":
          return `<table>
              <thead>
                <tr>
                  ${blok.table.thead
                    .map((th: any) => `<th>${th.value}</th>`.trim())
                    .join("")}
                </tr>
              </thead>
              <tbody>
                ${blok.table.tbody
                  .map((tr: any) =>
                    `
                  <tr>
                    ${tr.body
                      .map((td: any) => `<td>${td.value}</td>`.trim())
                      .join("")}
                  </tr>`.trim()
                  )
                  .join("")}
              </tbody>
            </table>`;
        default:
          return "Resolver not defined";
      }
    },
  },
  components: {
    accommodation_overview: AccommodationOverview,
    accommodation: Accommodation,
    accordion_item: AccordionItem,
    accordion: Accordion,
    award_item: AwardItem,
    iiq_check: IiQCheck,
    awards: Awards,
    reviews: Reviews,
    backlink: Backlink,
    banner_item: BannerItem,
    banner: Banner,
    body_text: BodyText,
    booking: Booking,
    box_grid: BoxGrid,
    box: Box,
    button: Button,
    campsite_overview: CampsiteOverview,
    campsite: Campsite,
    careers: Careers,
    carousel: Carousel,
    catalog_teaser: CatalogTeaser,
    catalog: Catalog,
    category_overview: CategoryOverview,
    category: Category,
    config: Config,
    contact: Contact,
    contact_teaser: ContactTeaser,
    container: Container,
    cycle_route_overview: CycleRouteOverview,
    cycle_route: CycleRoute,
    email: Email,
    event_overview: EventOverview,
    event: Event,
    experience: Experience,
    experiences: Experiences,
    experience_section: ExperienceSection,
    restaurant_teaser: RestaurantTeaser,
    restaurants: Restaurants,
    faq: Faq,
    featured_accommodations: FeaturedAccommodations,
    featured_campsites: FeaturedCampsites,
    featured_categories: FeaturedCategories,
    featured_items: FeaturedItems,
    featured_pitches: FeaturedPitches,
    grid: Grid,
    headline: Headline,
    image_gallery: ImageGallery,
    image_teaser: ImageTeaser,
    job_overview: JobOverview,
    job: Job,
    list_item: AccordionItem,
    list: List,
    map: Map,
    menu_button: MenuButton,
    menu_link: MenuLink,
    news_overview: NewsOverview,
    news: News,
    newsletter: Newsletter,
    offer_overview: OfferOverview,
    offer_teaser: OfferTeaser,
    offer: Offer,
    office: Office,
    opening_hours: OpeningHours,
    page: Page,
    pitch: Pitch,
    price_list_boxes: PriceListBoxes,
    price_list_item: PriceListItem,
    price_list: PriceList,
    prices: Prices,
    rich_text: RichText,
    special_offer: SpecialOffer,
    special_opening_hours: SpecialOpeningHours,
    stage: Stage,
    tab_item: TabItem,
    table_grid: TableGrid,
    table: Table,
    tabs: Tabs,
    text_list_boxes: TextListBoxes,
    text_list: TextList,
    text_image: TextImage,
    text_teaser: TextTeaser,
    travel: Travel,
  },
  enableFallbackComponent: false,
  customFallbackComponent: CustomFallbackComponent,
});

type StoryblokProviderProps = {
  children: ReactElement;
};

export default function StoryblokProvider({
  children,
}: StoryblokProviderProps) {
  return children;
}
